import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "~/api/provider";
import { ABILITY, type Flow } from "~/api/types";

export const FLOW_PURPOSES = ["reusable", "task-specific", "unclassified"] as const;
export type FlowPurpose = (typeof FLOW_PURPOSES)[number];
export const FLOW_PURPOSE_LABELS = {
  reusable: "Reusable methods",
  "task-specific": "Task-specific flows",
  unclassified: "Unclassified flows",
};
export function flowPurpose(flow: Pick<Flow, "categories">): FlowPurpose {
  const matches = FLOW_PURPOSES.filter((p) => p !== "unclassified" && flow.categories.includes(p));
  return matches.length === 1 ? (matches[0] ?? "unclassified") : "unclassified";
}

export function FlowLibraryFilter({
  flows,
  value,
  onChange,
}: {
  flows: Flow[];
  value: "all" | FlowPurpose;
  onChange: (value: "all" | FlowPurpose) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      Flow library
      <select
        className="rounded border border-divider bg-bg px-3 py-2"
        value={value}
        onChange={(e) => onChange(e.target.value as "all" | FlowPurpose)}
      >
        <option value="all">All flows ({flows.length})</option>
        {FLOW_PURPOSES.map((p) => (
          <option key={p} value={p}>
            {FLOW_PURPOSE_LABELS[p]} ({flows.filter((f) => flowPurpose(f) === p).length})
          </option>
        ))}
      </select>
    </label>
  );
}

export function FlowPurposeEditor({
  flow,
  onChanged,
}: {
  flow: Flow;
  onChanged: (categories: string[]) => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function change(purpose: FlowPurpose) {
    setBusy(true);
    setError("");
    try {
      onChanged((await api.automation.flows.classify(flow.slug, purpose)).categories);
      void queryClient.invalidateQueries({ queryKey: ["automation", "flows"] });
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-col gap-1 text-sm">
      <label className="flex flex-col gap-1">
        Flow purpose
        <select
          className="rounded border border-divider bg-bg px-3 py-2"
          value={flowPurpose(flow)}
          disabled={busy || !flow.abilities[ABILITY.flowUpdate]}
          onChange={(e) => void change(e.target.value as FlowPurpose)}
        >
          {FLOW_PURPOSES.map((p) => (
            <option key={p} value={p}>
              {FLOW_PURPOSE_LABELS[p]}
            </option>
          ))}
        </select>
      </label>
      <p className="text-xs text-text-secondary">
        Reusable methods accept different research briefs. Task-specific flows deliver one named
        outcome. Classification saves immediately without changing prompts or run history.
      </p>
      {error && (
        <p role="alert" className="text-danger">
          {error}
        </p>
      )}
    </div>
  );
}
